/**
 * Retour detaille apres une tentative.
 *
 * L'ancien message ("Essayez encore ! Verifiez que vous avez bien respecte
 * toutes les conditions.") n'indiquait pas laquelle avait echoue, alors que
 * l'information etait disponible : le moteur evaluait deja chaque condition
 * separement avant de n'en conserver que l'agregat.
 *
 * Les libelles sont derives de la nature de la condition, pas de sa syntaxe :
 * l'eleve lit "il manque une balise <h1>" plutot que la chaine de
 * configuration brute.
 */

import React from 'react';
import { Alert } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ConditionResult, WinEvaluation } from '../core/types';

interface WinConditionFeedbackProps {
  evaluation: WinEvaluation | null;
  /** Affiche aussi les conditions deja satisfaites. */
  showPassed?: boolean;
}

/** Formule une condition en langage comprehensible par l'eleve. */
export const useConditionLabel = () => {
  const { t } = useTranslation();

  return (result: ConditionResult): string => {
    switch (result.kind) {
      case 'html-element':
        return t('htmlCss.conditions.htmlElement', { tag: result.subject });
      case 'css-property':
        return t('htmlCss.conditions.cssProperty', {
          property: result.subject,
          value: result.expected ?? '',
        });
      case 'css-selector':
        return t('htmlCss.conditions.cssSelector', { selector: result.subject });
      default:
        return t('htmlCss.conditions.text', { text: result.subject });
    }
  };
};

const WinConditionFeedback: React.FC<WinConditionFeedbackProps> = ({
  evaluation,
  showPassed = true,
}) => {
  const { t } = useTranslation();
  const describe = useConditionLabel();

  // Rien a montrer tant que l'eleve n'a pas execute son code, et rien non plus
  // pour un exercice libre sans condition.
  if (!evaluation || evaluation.status === 'no-conditions') return null;

  if (evaluation.status === 'passed') {
    return (
      <Alert
        type="success"
        showIcon
        className="htmlcss-feedback"
        message={t('htmlCss.feedback.successTitle')}
      />
    );
  }

  const listed = showPassed ? evaluation.results : evaluation.failed;

  return (
    <Alert
      type="warning"
      showIcon
      className="htmlcss-feedback"
      message={t('htmlCss.feedback.failureTitle', { count: evaluation.failed.length })}
      description={
        <ul className="htmlcss-feedback-list">
          {listed.map((result, index) => (
            <li
              key={`${result.condition}-${index}`}
              className={result.passed ? 'is-passed' : 'is-failed'}
            >
              {result.passed ? (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
              ) : (
                <CloseCircleTwoTone twoToneColor="#ff4d4f" />
              )}
              <span>{describe(result)}</span>
            </li>
          ))}
        </ul>
      }
    />
  );
};

export default WinConditionFeedback;
